import { isCounterClockwise, polygonArea } from '../geo/sphere.js';
import { normalise, add } from '../geo/vec3.js';
import { Gnomonic, bounds2, centroid2, contains2 } from './gnomonic.js';
/**
 * City blocks.
 *
 * The power diagram is the right tool for continents and countries, where
 * borders should look like borders — long, irregular, argued over. It is the
 * wrong tool for the smallest places: thirty-seven cells packed into one city
 * come out as a strangled pie chart, every plot a wedge pointing at the middle.
 *
 * So the deepest level is laid out as blocks instead: a squarified treemap
 * inside the largest rectangle the city can hold, which gives neat plots in
 * near-square proportions with open ground left around the edge. Areas stay
 * proportional to weight exactly, because nothing is clipped — the grid is
 * placed where it is known to fit rather than trimmed to shape afterwards.
 */
export function blocks(parentRing, targets) {
    if (targets.length === 0)
        return [];
    const centre = normalise(parentRing.reduce((acc, v) => add(acc, v), { x: 0, y: 0, z: 0 }));
    const projection = new Gnomonic(centre);
    const flat = projection.projectRing(parentRing);
    if (!flat)
        return [];
    const rect = inscribedRect(flat);
    if (!rect)
        return [];
    const plots = squarify(targets, rect);
    // The gnomonic projection stretches badly once a polygon covers a wide angle,
    // and an "inscribed" rectangle measured in a stretched plane can sit well
    // outside the real shape — plots for one very large city landed seventy
    // degrees away, off the territory entirely. Rather than trust the rectangle,
    // check the plots actually landed in the parent, and hand the job back if
    // they did not.
    const inside = plots.every(({ box }) => [
        { x: box.minX, y: box.minY },
        { x: box.maxX, y: box.minY },
        { x: box.maxX, y: box.maxY },
        { x: box.minX, y: box.maxY },
    ].every((corner) => contains2(flat, corner)));
    if (!inside)
        return [];
    return plots.map(({ id, box }) => {
        const ring = projection.unprojectRing([
            { x: box.minX, y: box.minY },
            { x: box.maxX, y: box.minY },
            { x: box.maxX, y: box.maxY },
            { x: box.minX, y: box.maxY },
        ]);
        // Whether a corner order comes out clockwise on the sphere depends on the
        // projection's handedness, and a ring wound the wrong way builds a surface
        // that faces into the planet: the plots were all there and all invisible.
        if (!isCounterClockwise(ring))
            ring.reverse();
        const site = projection.unproject({
            x: (box.minX + box.maxX) / 2,
            y: (box.minY + box.maxY) / 2,
        });
        return { id, ring, area: polygonArea(ring), site, weight: 0 };
    });
}
/**
 * The largest axis-aligned rectangle that fits inside a convex polygon, found
 * by growing one about the centroid until a corner leaves.
 *
 * A rectangle sits inside a convex polygon exactly when its four corners do,
 * which makes the test cheap and the search a plain bisection. Cells from the
 * partition engine are always convex, so this holds wherever it is used.
 */
function inscribedRect(ring, margin = 0.94) {
    const box = bounds2(ring);
    const halfWidth = (box.maxX - box.minX) / 2;
    const halfHeight = (box.maxY - box.minY) / 2;
    if (halfWidth <= 0 || halfHeight <= 0)
        return null;
    // Two candidate centres. The projection's own origin is the one point
    // guaranteed to lie inside the polygon, and is the reliable choice once the
    // shape covers a wide angle — a centroid measured in the stretched plane
    // drifts, which is how plots once ended up outside their own city. The flat
    // centroid usually allows a bigger grid though, so both are tried and the
    // larger rectangle that genuinely fits wins.
    const candidates = [];
    if (contains2(ring, { x: 0, y: 0 }))
        candidates.push({ x: 0, y: 0 });
    const flatMiddle = centroid2(ring);
    if (contains2(ring, flatMiddle))
        candidates.push(flatMiddle);
    if (candidates.length === 0)
        return null;
    let best = null;
    let bestArea = 0;
    for (const middle of candidates) {
        const fits = (scale) => {
            const w = halfWidth * scale;
            const h = halfHeight * scale;
            return (contains2(ring, { x: middle.x - w, y: middle.y - h }) &&
                contains2(ring, { x: middle.x + w, y: middle.y - h }) &&
                contains2(ring, { x: middle.x - w, y: middle.y + h }) &&
                contains2(ring, { x: middle.x + w, y: middle.y + h }));
        };
        let low = 0;
        let high = 1;
        for (let i = 0; i < 24; i += 1) {
            const mid = (low + high) / 2;
            if (fits(mid))
                low = mid;
            else
                high = mid;
        }
        if (low <= 1e-6)
            continue;
        const scale = low * margin;
        const rect = {
            minX: middle.x - halfWidth * scale,
            maxX: middle.x + halfWidth * scale,
            minY: middle.y - halfHeight * scale,
            maxY: middle.y + halfHeight * scale,
        };
        const area = (rect.maxX - rect.minX) * (rect.maxY - rect.minY);
        if (area > bestArea) {
            best = rect;
            bestArea = area;
        }
    }
    return best;
}
/**
 * Squarified treemap: fills the rectangle row by row, adding to a row while
 * that keeps its plots closer to square and starting a new one when it stops.
 * The result is a grid rather than a set of slivers, which is the whole point.
 */
function squarify(targets, rect) {
    const total = targets.reduce((sum, t) => sum + Math.max(t.share, 1e-9), 0);
    const area = (rect.maxX - rect.minX) * (rect.maxY - rect.minY);
    const items = targets.map((t) => ({
        id: t.id,
        area: (Math.max(t.share, 1e-9) / total) * area,
    }));
    // Largest first, which is what keeps the proportions tidy.
    items.sort((a, b) => b.area - a.area);
    const out = [];
    let free = { ...rect };
    let row = [];
    const width = () => Math.min(free.maxX - free.minX, free.maxY - free.minY);
    /** Worst aspect ratio in a row, which is the thing being minimised. */
    const worst = (candidate, side) => {
        if (candidate.length === 0 || side <= 0)
            return Infinity;
        const sum = candidate.reduce((acc, item) => acc + item.area, 0);
        if (sum <= 0)
            return Infinity;
        const max = Math.max(...candidate.map((i) => i.area));
        const min = Math.min(...candidate.map((i) => i.area));
        const side2 = side * side;
        const sum2 = sum * sum;
        return Math.max((side2 * max) / sum2, sum2 / (side2 * min));
    };
    const placeRow = () => {
        if (row.length === 0)
            return;
        const sum = row.reduce((acc, item) => acc + item.area, 0);
        const horizontal = free.maxX - free.minX >= free.maxY - free.minY;
        if (horizontal) {
            const depth = sum / (free.maxY - free.minY);
            let y = free.minY;
            for (const item of row) {
                const height = (item.area / sum) * (free.maxY - free.minY);
                out.push({ id: item.id, box: { minX: free.minX, maxX: free.minX + depth, minY: y, maxY: y + height } });
                y += height;
            }
            free = { ...free, minX: free.minX + depth };
        }
        else {
            const depth = sum / (free.maxX - free.minX);
            let x = free.minX;
            for (const item of row) {
                const boxWidth = (item.area / sum) * (free.maxX - free.minX);
                out.push({ id: item.id, box: { minX: x, maxX: x + boxWidth, minY: free.minY, maxY: free.minY + depth } });
                x += boxWidth;
            }
            free = { ...free, minY: free.minY + depth };
        }
        row = [];
    };
    for (const item of items) {
        const side = width();
        if (row.length > 0 && worst([...row, item], side) > worst(row, side))
            placeRow();
        row.push(item);
    }
    placeRow();
    return out;
}
//# sourceMappingURL=blocks.js.map